import {
  ComponentType,
  ReactNode,
  Suspense,
  lazy,
  useEffect,
  useState
} from 'react';
import { RouteObject, useRoutes } from 'react-router-dom';
import { DefaultLoader } from '@/components/hugin/custom/Loader/v1';
import { layoutsMap } from '@/layouts/layoutsMap';
import { pagesMap } from '@/pages/pagesMap';
import ProtectedRoute from './ProtectedRoute';
import { fetchRoutesFromApi } from './routesApi';
import type { AppRouteChild, AppRouteConfig } from './types';

function lazyWrap(
  importer: () => Promise<{ default: ComponentType }>,
  cacheKey: string
): ReactNode {
  let Component = lazyPageCache.get(cacheKey);

  if (!Component) {
    Component = lazy(importer);
    lazyPageCache.set(cacheKey, Component);
  }

  return (
    <Suspense fallback={<DefaultLoader />}>
      <Component />
    </Suspense>
  );
}

const lazyPageCache = new Map<string, ReturnType<typeof lazy>>();

function resolveElement(name: string): ReactNode {
  if (!name) return <div>Brak elementu</div>;

  const elementName = name === 'Redirect404' ? 'NotFound' : name;
  const importer = pagesMap[elementName];

  if (!importer || typeof importer !== 'function') {
    return <div>Nieznana strona: {elementName}</div>;
  }

  return lazyWrap(importer, elementName);
}

function mapChildRoute(child: AppRouteChild): RouteObject {
  let element = resolveElement(child.element);

  if (child.requiresAuth || child.requiredPermission) {
    element = (
      <ProtectedRoute requiredPermission={child.requiredPermission}>
        {element}
      </ProtectedRoute>
    );
  }

  return {
    index: child.index,
    path: child.path ?? undefined,
    element
  };
}

function mapRoutesFromApi(apiRoutes: AppRouteConfig[]): RouteObject[] {
  return apiRoutes.map((route) => {
    const Layout = layoutsMap[route.layout];
    const isProtected = route.element === 'ProtectedRoute';

    let element = Layout ? (
      lazyWrap(Layout, `layout:${route.layout}`)
    ) : (
      <div>Nieznany layout</div>
    );

    if (isProtected) {
      element = (
        <ProtectedRoute requiredPermission={route.requiredPermission}>
          {element}
        </ProtectedRoute>
      );
    }

    return {
      path: route.path,
      element,
      children: (route.children ?? []).map(mapChildRoute)
    };
  });
}

const loadingRoutes: RouteObject[] = [
  {
    path: '*',
    element: <DefaultLoader />
  }
];

export default function AppRouter() {
  const [routesData, setRoutesData] = useState<RouteObject[]>(loadingRoutes);

  useEffect(() => {
    fetchRoutesFromApi()
      .then((data) => {
        setRoutesData([
          ...mapRoutesFromApi(data),
          {
            path: '*',
            element: resolveElement('NotFound')
          }
        ]);
      })
      .catch(() => {
        setRoutesData([
          {
            path: '*',
            element: resolveElement('NotFound')
          }
        ]);
      });
  }, []);

  return useRoutes(routesData);
}
